import { MANUAL_NOTE_MAX } from '@barso/shared';
import { useEffect, useRef, useState } from 'react';
import { Keypad } from '../components/Keypad';
import { Button, TextField } from '../components/ui';
import { euro } from '../format';
import { pushDigits, removeLastDigit } from '../pos/keypad';
import { isManualLineError, makeManualLine } from '../pos/manualLine';
import { captureStill, shrinkFile } from '../pos/photoShrink';
import type { CartLine } from '../pos/types';
import { Sheet } from './posParts';
import '../components/reasonChips.css';

/** Ergebnis des Blatts: die Zeile für den Warenkorb und das schon verkleinerte Foto (falls aufgenommen). */
export interface ManualItemResult {
  line: CartLine;
  photo: ArrayBuffer | null;
}

/**
 * Blatt „Artikel ohne Etikett erfassen“ (U17h): Preis (Pflicht, Ziffernblock), Verkäufernummer (freiwillig; die Kasse sagt nur
 * „Verkäufer 17 ist im Stand bekannt“, nie einen Namen), kurze Beschreibung (bis 300 Zeichen) und ein Foto (freiwillig, ein
 * Einzelbild aus der laufenden Kamera, sonst eine Datei bzw. die Gerätekamera). „In den Warenkorb“ legt die Zeile „Ohne Etikett ·
 * V17 · 6,00 €“ an. Die Zeile hat keinen Etikettencode (Regel 4).
 */
export function ManualItemSheet({
  sellerKnown,
  onAdd,
  onClose,
}: {
  /** Ist diese Verkäufernummer im Stand der Kasse bekannt? Fehlt die Prüfung, wird nichts gezeigt. */
  sellerKnown?: (sellerNumber: number) => Promise<boolean>;
  onAdd: (result: ManualItemResult) => void;
  onClose: () => void;
}) {
  // Ganze Cent, nie Text oder Gleitkomma (K6)
  const [cents, setCents] = useState(0);
  const [seller, setSeller] = useState('');
  const [note, setNote] = useState('');
  const [photo, setPhotoBytes] = useState<ArrayBuffer | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [photoMessage, setPhotoMessage] = useState<string | null>(null);
  const [knownFor, setKnownFor] = useState<{ number: number; ok: boolean } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const urlRef = useRef<string | null>(null);

  const sellerNumber = /^\d{1,6}$/.test(seller) ? Number(seller) : undefined;
  const sellerInvalid = seller !== '' && (sellerNumber === undefined || sellerNumber < 1);
  // Nur das Ergebnis zur aktuell getippten Nummer zählt (ein älteres Ergebnis zu einer anderen Nummer wird nicht gezeigt)
  const known = knownFor !== null && knownFor.number === sellerNumber ? knownFor.ok : null;

  useEffect(() => {
    if (sellerNumber === undefined || sellerNumber < 1 || sellerKnown === undefined) return;
    const guard = { cancelled: false };
    void sellerKnown(sellerNumber).then((ok) => {
      if (!guard.cancelled) setKnownFor({ number: sellerNumber, ok });
    });
    return () => {
      guard.cancelled = true;
    };
  }, [sellerNumber, sellerKnown]);

  // Die Adresse der Vorschau wird freigegeben, sobald das Foto wechselt oder das Blatt schließt
  useEffect(
    () => () => {
      if (urlRef.current !== null) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );
  function setPhoto(bytes: ArrayBuffer | null): void {
    if (urlRef.current !== null) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    if (bytes !== null && typeof URL.createObjectURL === 'function') {
      urlRef.current = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }));
    }
    setPhotoBytes(bytes);
    setPreviewUrl(urlRef.current);
  }

  async function takeStill(): Promise<void> {
    setPhotoMessage(null);
    try {
      const video = document.querySelector<HTMLVideoElement>('video.camera__video');
      const still = await captureStill(video);
      if (still === null) {
        // Kein laufendes Kamerabild: Gerätekamera bzw. Datei wählen
        fileInput.current?.click();
        return;
      }
      setPhoto(still);
    } catch {
      setPhotoMessage('Das Foto konnte nicht aufgenommen werden. Der Artikel geht auch ohne Foto.');
    }
  }

  async function takeFile(file: File | undefined): Promise<void> {
    if (file === undefined) return;
    setPhotoMessage(null);
    try {
      const bytes = await shrinkFile(file);
      if (bytes === null) {
        setPhotoMessage('Das Foto ist zu groß geworden. Der Artikel geht auch ohne Foto.');
        return;
      }
      setPhoto(bytes);
    } catch {
      setPhotoMessage('Das Foto konnte nicht gelesen werden. Der Artikel geht auch ohne Foto.');
    }
  }

  const built =
    cents === 0 || sellerInvalid
      ? null
      : makeManualLine({
          uuid: 'preview',
          priceCents: cents,
          sellerNumber,
          note,
        });
  const priceProblem =
    cents !== 0 && built !== null && isManualLineError(built) && built === 'price';
  const ready = built !== null && !isManualLineError(built);

  return (
    <Sheet title="Artikel ohne Etikett erfassen" dismissible={false} onClose={onClose}>
      <p className="cash__given" aria-live="polite">
        Preis: <strong>{cents === 0 ? '–' : euro(cents)}</strong>
      </p>
      {priceProblem && (
        <p className="field__error" role="alert">
          Der Preis muss zwischen 0,50 € und 5.000 € liegen.
        </p>
      )}
      <Keypad
        onDigits={(digits) => {
          setCents((value) => pushDigits(value, digits));
        }}
        onBackspace={() => {
          setCents(removeLastDigit);
        }}
      />
      <TextField
        label="Verkäufernummer (freiwillig)"
        inputMode="numeric"
        autoComplete="off"
        value={seller}
        maxLength={6}
        error={sellerInvalid ? 'Bitte nur Ziffern, ab 1.' : undefined}
        hint={
          known === true && sellerNumber !== undefined
            ? `Verkäufer ${String(sellerNumber)} ist im Stand bekannt.`
            : known === false
              ? 'Diese Nummer ist im Stand der Kasse nicht bekannt. Die Leitung prüft sie.'
              : undefined
        }
        onChange={(event) => {
          setSeller(event.target.value.trim());
        }}
      />
      <TextField
        label="Beschreibung (freiwillig)"
        hint="Zum Beispiel „blaue Kinderjacke, Etikett fehlt“. Bitte keine Namen oder Kontaktdaten."
        value={note}
        maxLength={MANUAL_NOTE_MAX}
        autoComplete="off"
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      <span className="reason__counter" aria-hidden="true">
        {`${String(Array.from(note).length)}/${String(MANUAL_NOTE_MAX)}`}
      </span>
      <section className="stack" aria-label="Foto">
        <h3>Foto (freiwillig)</h3>
        {previewUrl !== null && (
          <img
            src={previewUrl}
            alt="Vorschau des Fotos"
            style={{ maxWidth: '100%', maxHeight: '12rem', objectFit: 'contain' }}
          />
        )}
        <p className="muted">Nur den Artikel oder das Schild fotografieren, keine Personen.</p>
        {photoMessage !== null && (
          <p className="muted" role="status">
            {photoMessage}
          </p>
        )}
        <div className="row">
          <Button
            variant="secondary"
            onClick={() => {
              void takeStill();
            }}
          >
            {photo === null ? 'Foto aufnehmen' : 'Neu aufnehmen'}
          </Button>
          {photo !== null && (
            <Button
              icon="trash"
              variant="secondary"
              onClick={() => {
                setPhoto(null);
              }}
            >
              Foto entfernen
            </Button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          aria-label="Foto auswählen"
          onChange={(event) => {
            void takeFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </section>
      <div className="row">
        <Button
          disabled={!ready}
          onClick={() => {
            const line = makeManualLine({
              uuid: crypto.randomUUID(),
              priceCents: cents,
              sellerNumber,
              note,
              // Die Kennung des Fotos wird erst vom Warenkorb vergeben, wenn es gespeichert ist (siehe `onAdd`)
            });
            if (isManualLineError(line)) return;
            onAdd({ line, photo });
          }}
        >
          In den Warenkorb
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}
